import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { ArrowUpIcon, KeepGapIcon, RippleTrackIcon } from './icons';

type RippleMode = 'ripple-track' | 'keep-gap';

export interface RippleModeOption {
  id: RippleMode;
  name: string;
  description: string;
  icon: React.ReactNode;
}

export interface RippleModeDropdownProps {
  value: RippleMode;
  onChange: (mode: RippleMode) => void;
  disabled: boolean;
}

const rippleModeOptions: RippleModeOption[] = [
  {
    id: 'ripple-track',
    name: 'Ripple Mode',
    description: 'Shift clips on same track',
    icon: <RippleTrackIcon size={12} color="currentColor" />,
  },
  {
    id: 'keep-gap',
    name: 'Gap Mode',
    description: 'Leave empty space',
    icon: <KeepGapIcon size={12} color="currentColor" />,
  },
];

const RippleModeDropdown: React.FC<RippleModeDropdownProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentOption = rippleModeOptions.find(opt => opt.id === value);

  useOutsideAlerter(dropdownRef, () => {
    setIsOpen(false);
  });

  const handleSelect = (mode: RippleMode) => {
    onChange(mode);
    setIsOpen(false);
  };

  return (
    <>
      <DropdownContainer ref={dropdownRef}>
        <DropdownTrigger
          open={isOpen}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <TriggerContent>
            <OptionIcon size={14}>{currentOption?.icon}</OptionIcon>
            {currentOption?.name}
          </TriggerContent>
          <ArrowUpIcon
            color={disabled ? 'var(--text-muted)' : 'var(--accent)'}
            stroke={disabled ? 'var(--text-muted)' : 'var(--accent)'}
            rotate="180deg"
          />
        </DropdownTrigger>

        {isOpen && (
          <DropdownMenu>
            <DropdownSectionTitle>Delete Mode</DropdownSectionTitle>
            {rippleModeOptions.map(option => (
              <DropdownOption
                key={option.id}
                isActive={option.id === value}
                onClick={() => handleSelect(option.id)}
                data-tooltip-id={`${option.id}-tooltip`}
                data-tooltip-content={option.description}
              >
                <OptionIcon
                  className="option-icon"
                  size={12}
                  style={{ marginTop: '3px' }}
                >
                  {option.icon}
                </OptionIcon>
                <OptionContent>
                  <OptionName className="option-name">{option.name}</OptionName>
                  <OptionDescription>{option.description}</OptionDescription>
                </OptionContent>
              </DropdownOption>
            ))}
          </DropdownMenu>
        )}
      </DropdownContainer>
    </>
  );
};

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownTrigger = styled.button<{ open: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  background-color: transparent;
  gap: 8px;
  width: 135px;
  height: 30px;
  padding: 8px;
  border: none;
  border-radius: var(--radius-button);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 150ms ease-out;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }

  &:active {
    background-color: var(--gray-700);
    scale: 0.98;
  }

  &:disabled {
    color: var(--text-muted);
    opacity: 0.7;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const TriggerContent = styled.div`
  display: flex;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  align-items: center;
  gap: 8px;
`;

const DropdownMenu = styled.div`
  position: absolute;
  left: 0px;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  width: max-content;
  min-width: 160px;
  z-index: 1000;
  box-shadow: rgba(0, 0, 0, 0.5) 0px 4px 12px;
  box-sizing: border-box;
`;

const DropdownOption = styled.div<{ isActive?: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  cursor: pointer;
  transition: var(--transition-fast);

  color: ${({ isActive }) =>
    isActive ? 'var(--primary-hover)' : 'var(--text-secondary)'};
  .option-icon {
    color: ${({ isActive }) =>
      isActive ? 'var(--primary-hover)' : 'var(--text-secondary)'};
  }
  .option-name {
    color: ${({ isActive }) =>
      isActive ? 'var(--primary-hover)' : 'var(--text-secondary)'};
  }
  &:hover {
    background-color: var(--border);
  }
`;

const OptionIcon = styled.div<{ size: number }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;

  svg {
    width: ${props => props.size}px;
    height: ${props => props.size}px;
  }
`;

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionName = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  transition: var(--transition-fast);
`;

const OptionDescription = styled.div`
  font-size: var(--text-xs);
  color: var(--text-secondary);
`;

const DropdownSectionTitle = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border);
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
`;

export default RippleModeDropdown;
